import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Link2, Save } from 'lucide-react';

const CommitteeLandingPageTab = ({
  committee,
  landingPageForm,
  setLandingPageForm,
  saveLandingPage,
  savingLandingPage
}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const landingUrl = landingPageForm.slug ? `${baseUrl}/${landingPageForm.slug}` : '';

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-8" data-testid="committee-landing-editor">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-heading text-xl font-semibold text-slate-900">Committee Landing Page</h3>
          <p className="text-sm text-slate-600">Edit the public landing page for this committee.</p>
        </div>
        <Button
          onClick={saveLandingPage}
          disabled={savingLandingPage || !landingPageForm.slug}
          className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
          data-testid="save-landing-page-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {savingLandingPage ? 'Saving...' : 'Save Landing Page'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="committee-slug">Landing Page Slug *</Label>
          <Input
            id="committee-slug"
            value={landingPageForm.slug}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, slug: e.target.value })}
            placeholder="e.g., ottawa"
            data-testid="landing-slug-input"
          />
          <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="landing-url-display">
            <Link2 className="w-3.5 h-3.5" />
            {landingUrl || 'Enter a slug to generate the landing page URL.'}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="landing-hero-title">Hero Title</Label>
          <Input
            id="landing-hero-title"
            value={landingPageForm.hero_title}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, hero_title: e.target.value })}
            placeholder={committee?.name ? `${committee.name} Scholarship Program` : 'Committee Scholarship Program'}
            data-testid="landing-hero-title-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="landing-hero-subtitle">Hero Subtitle</Label>
        <Textarea
          id="landing-hero-subtitle"
          value={landingPageForm.hero_subtitle}
          onChange={(e) => setLandingPageForm({ ...landingPageForm, hero_subtitle: e.target.value })}
          placeholder="Short statement about the scholarship program"
          rows={2}
          data-testid="landing-hero-subtitle-input"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="landing-about-title">About Section Title</Label>
          <Input
            id="landing-about-title"
            value={landingPageForm.about_title}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, about_title: e.target.value })}
            placeholder="About the Committee"
            data-testid="landing-about-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="landing-scholarships-title">Scholarships Section Title</Label>
          <Input
            id="landing-scholarships-title"
            value={landingPageForm.scholarships_title}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, scholarships_title: e.target.value })}
            placeholder="Active Scholarships"
            data-testid="landing-scholarships-title-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="landing-about-description">About Description</Label>
        <Textarea
          id="landing-about-description"
          value={landingPageForm.about_description}
          onChange={(e) => setLandingPageForm({ ...landingPageForm, about_description: e.target.value })}
          placeholder="Describe the committee's scholarship mission"
          rows={4}
          data-testid="landing-about-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="landing-scholarships-subtitle">Scholarships Section Subtitle</Label>
        <Textarea
          id="landing-scholarships-subtitle"
          value={landingPageForm.scholarships_subtitle}
          onChange={(e) => setLandingPageForm({ ...landingPageForm, scholarships_subtitle: e.target.value })}
          placeholder="Short introduction above the scholarships list"
          rows={2}
          data-testid="landing-scholarships-subtitle-input"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="landing-contact-email">Contact Email</Label>
          <Input
            id="landing-contact-email"
            value={landingPageForm.contact_email}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, contact_email: e.target.value })}
            placeholder="committee@example.com"
            data-testid="landing-contact-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="landing-contact-address">Contact Address</Label>
          <Input
            id="landing-contact-address"
            value={landingPageForm.contact_address}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, contact_address: e.target.value })}
            placeholder="123 Main St, City"
            data-testid="landing-contact-address-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="landing-logo-url">Logo Image URL</Label>
          <Input
            id="landing-logo-url"
            value={landingPageForm.logo_url}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, logo_url: e.target.value })}
            placeholder="https://.../logo.png"
            data-testid="landing-logo-url-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="landing-banner-url">Banner Image URL</Label>
          <Input
            id="landing-banner-url"
            value={landingPageForm.banner_url}
            onChange={(e) => setLandingPageForm({ ...landingPageForm, banner_url: e.target.value })}
            placeholder="https://.../banner.jpg"
            data-testid="landing-banner-url-input"
          />
        </div>
      </div>
    </div>
  );
};

export default CommitteeLandingPageTab;
